<template>
  <div
    v-if="product"
    :class="[
      'product',
      useCards ? 'card' : ''
    ]"
  >
    <h3 v-if="showTitle">
      SEO platform price
    </h3>
    <p>
      This is the price you will charge if a customer wants to sign up to the platform only.
    </p>

    <div class="form-group">
      <label>Name</label>
      <input
        type="text"
        :class="[
          'form-control',
          errors['softwareProduct.name'] ? 'text-danger' : '',
        ]"
        :placeholder="product.fallbackName"
        :value="product.name"
        maxlength="255"
        @input="setSoftwareProductField({ name: $event.target.value })"
      >
      <small>
        Enter the name you wish to use for platform-only sales. For example, “My Agency SEO Platform”
      </small>
      <small
        v-if="errors['softwareProduct.name']"
        class="text-danger"
        v-html="errors['softwareProduct.name']"
      />
    </div>

    <div v-if="isWizard && addonsConfigured">
      <p class="text-danger">
        It looks like you've configured some add-on prices.<br>
        Modifying your software pricing will affect which add-ons you are able to sell.
      </p>
      <p class="text-danger">
        <a
          href="javascript:;"
          @click="clearAddonConfiguration"
        >
          Click here
        </a> to clear add-on configuration and edit software pricing.
      </p>
    </div>
    <div v-else>
      <div class="currency_select">
        <div>
          <h4>Prices:</h4>
          <small>
            This product can be set to be billed in either USD, GBP, or both.
          </small>
          <p
            v-if="errors['softwareProduct.supportedCurrencies']"
            class="text-danger"
          >
            {{ errors['softwareProduct.supportedCurrencies'] }}
          </p>
          <p
            v-if="errors['softwareProduct.prices']"
            class="text-danger"
          >
            {{ errors['softwareProduct.prices'] }}
          </p>
        </div>
      </div>
      <div
        v-for="(data, currencyCode) in supportedCurrencies"
        :key="currencyCode"
      >
        <div class="currency_toggle">
          <span
            :class="[
              'temp_radio',
              supportedCurrencies[currencyCode].enabled ?
                'active' :
                '',
            ]"
            @click="toggleEnabledCurrency(currencyCode)"
          >
            <svg-vue
              v-if="true"
              icon="icons/checkmark"
            />
            <span v-else>&nbsp;</span>
          </span>
          <strong>{{ currencyCode }}</strong>
        </div>
        <accordion-transition>
          <product-price-inputs
            v-if="supportedCurrencies[currencyCode].enabled"
            :currency-code="currencyCode"
            :price="product.prices[currencyCode] ?? emptyPrice"
            :general-error-string="errors[`softwareProduct.prices.${currencyCode}`]"
            :minimum-prices="softwareMinimumPrices"
            :errors="{
              monthly: errors[`softwareProduct.prices.${currencyCode}.monthlyRaw`],
              annual: errors[`softwareProduct.prices.${currencyCode}.annualRaw`],
            }"
            :message-popup-content="softwareMinimumPopupContent"
            change-method="setSoftwareProductPriceField"
          />
        </accordion-transition>
      </div>
    </div>
  </div>

  <div
    class="product support-toggle"
    :class="{
      disabled: !(monthlyRequired || annualRequired) || tempUSDSupportBlock,
      card: useCards,
      inline: !useCards,
    }"
  >
    <p
      v-if="tempUSDSupportBlock"
      class="text-danger"
    >
      <em>This option is temporarily unavailable for merchants offering only USD prices.</em>
      <br>
      <em>In the meantime, you can still complete your setup and add this product as soon as it is available</em>
    </p>
    <p v-else-if="!(monthlyRequired || annualRequired)">
      <em>Please define at least one price above to allow support add-on configuration.</em>
    </p>
    <div
      class="select_type"
      @click="toggleSupportAddon"
    >
      <span
        :class="[
          'temp_radio',
          enableSupportAddon ? 'active' : '',
        ]"
      >
        <svg-vue
          v-if="enableSupportAddon"
          icon="icons/checkmark"
        />
        <span v-else>&nbsp;</span>
      </span>
      <label>Sell a monthly support product too?</label>
    </div>
  </div>

  <accordion-transition>
    <div
      v-if="enableSupportAddon"
      :class="[
        'product',
        'height_transition',
        useCards ? 'card' : ''
      ]"
    >
      <h3>Support product price</h3>

      <p>
        If you want to offer a second product which gives the customer additional monthly support from your team
        alongside access to the platform, then set the price for this support below.
        <strong>Please note:</strong> You do not need to incorporate the price of the software; that will be added to
        the below to calculate the final price.
      </p>

      <div class="form-group">
        <label>Name</label>
        <input
          type="text"
          :class="[
            'form-control',
            errors['supportAddon.name'] ? 'text-danger' : '',
          ]"
          :placeholder="support.fallbackName"
          :value="support.name === null ? support.fallbackName : support.name"
          maxlength="255"
          @input="setSupportAddonField({ name: $event.target.value })"
        >
        <small
          v-if="errors['supportAddon.name']"
          class="text-danger"
          v-html="errors['supportAddon.name']"
        />
        <small>
          Enter the name you wish to use for platform and support sales. For example, “My Agency SEO Platform + 2-hour
          Monthly Support”
        </small>
      </div>

      <div
        v-for="(data, currencyCode) in supportedCurrencies"
        :key="currencyCode"
      >
        <accordion-transition>
          <product-price-inputs
            v-if="supportedCurrencies[currencyCode].enabled"
            :is-support-product="true"
            :show-currency="false"
            :currency-code="currencyCode"
            :price="support.prices[currencyCode] ?? emptyPrice"
            :minimum-prices="supportMinimumPrices"
            :general-error-string="errors[`supportAddon.prices.${currencyCode}`]"
            :errors="{
              monthly: errors[`supportAddon.prices.${currencyCode}.monthlyRaw`],
              annual: errors[`supportAddon.prices.${currencyCode}.annualRaw`],
            }"
            :show-monthly-price="monthlyRequired"
            :show-annual-price="annualRequired"
            :message-popup-content="supportMinimumPopupContent"
            change-method="setSupportAddonPriceField"
          />
        </accordion-transition>
      </div>
    </div>
  </accordion-transition>

  <button
    v-if="!hideButton"
    class="button"
    :disabled="loading"
    @click="validateAndSubmit"
  >
    <i
      v-if="loading"
      class="fas fa-spin fa-spinner"
    />
    Continue
  </button>
</template>

<script>
import AccordionTransition from "../../../../views/reports/components/global/transitions/AccordionTransition";
import ProductPriceInputs from "../setupWizardSteps/includes/ProductPriceInputs";
import {mapActions, mapGetters} from "vuex";
import Swal from "sweetalert2";

export default {
  components: {
    ProductPriceInputs,
    AccordionTransition,
  },
  props: {
    isWizard: {
      type: Boolean,
      default: false,
    },
    product: {
      type: Object,
      required: true,
    },
    support: {
      type: Object,
      required: true,
    },
    useCards: {
      type: Boolean,
      default: false,
    },
    showTitle: {
      type: Boolean,
      default: true,
    },
    hideButton: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'submit'
  ],
  computed: {
    tempUSDSupportBlock() { // TODO: Remove this and all references as part of HIKE-791
      const { prices } = this.product;
      return prices.length === 1
        && prices[0].currency === 'USD'
    },
    ...mapGetters('productManager', [
      'enableSupportAddon',
      'softwarePrices',
      'supportedCurrencies',
      'errors',
      'loading',
    ]),
    ...mapGetters('addonManager', [
      'addons',
    ]),
    emptyPrice() {
      return {
        monthlyRaw: 0,
        annualRaw: 0,
      }
    },
    monthlyRequired() {
      const { prices } = this.product;

      let monthlyRequired = false;

      Object.entries(this.supportedCurrencies)
        .forEach(([code]) => {
          if (
            prices[code] &&
            prices[code].monthlyRaw > 0
          ) {
            monthlyRequired = true;
          }
        });

      return monthlyRequired;
    },
    annualRequired() {
      const { prices } = this.product;

      let annualRequired = false;

      Object.entries(this.supportedCurrencies)
        .forEach(([code]) => {
          if (
            prices[code] &&
            prices[code].annualRaw > 0
          ) {
            annualRequired = true;
          }
        });

      return annualRequired;
    },
    addonsConfigured() {
      return (Object.keys(this.addons).length);
    },
    softwareMinimumPrices() {
      if (!this.softwarePrices) {
        return {};
      }

      return {
        GBP: { // TODO: Compute/map this somehow
          MONTH: this.softwarePrices['GBP']['MONTH'],
          YEAR: this.softwarePrices['GBP']['YEAR'],
        },
        USD: {
          MONTH: this.softwarePrices['USD']['MONTH'],
          YEAR: this.softwarePrices['USD']['YEAR'],
        },
      }
    },
    supportMinimumPrices() {
      return {
        GBP: {
          MONTH: {
            minimum: 100,
          },
          YEAR: {
            minimum: 100,
          },
        },
        USD: {
          MONTH: {
            minimum: 100,
          },
          YEAR: {
            minimum: 100,
          },
        },
      }
    },
    softwareMinimumPopupContent() {
      return `
        <h1>How we calculate minimum price</h1>
        <div style="font-size: 1rem;">
          <p style="line-height: 1.4rem;">
            Our minimum price is based on the maximum cost<span class="text-danger">*</span> you pay for the Hike
            software (including taxes, if applicable), plus Stripes payment handling fees (3% + a single 0.20 (£/$)
            charge).
          </p>
          <p style="line-height: 1.4rem;">
            <strong>So, to put it simply:</strong><br>
            Hike monthly software cost + Stripe fees = your minimum price.
          </p>
          <p style="line-height: 1.4rem;">
            These costs are calculated and deducted automatically via Stripe, so you don't have to worry about
            accounting for these charges within your pricing.
          </p>
          <p style="line-height: 1.4rem;">
            Please note; our charges will not be visible to your customers.
          </p>
          <p style="line-height: 1rem;">
            <small>
              <span class="text-danger">*</span> Maximum cost refers to the full rate charged by Hike for adding a
              website to the platform without any tiered discounts applied. This ensures that you do not set a price
              that falls below our fee, in the event that your tiered discount changes to a more expensive tier (via a
              reduction in the total number of websites in your account).
            </small>
          </p>
        </div>
      `;
    },
    supportMinimumPopupContent() {
      return `
        <h1>Minimum price for support product</h1>
        <div style="font-size: 1rem;">
          <p style="line-height: 1.4rem;">
            If you decide to offer a monthly support product, there is no minimum price as Hike does not retain any
            proportion of this revenue.
          </p>
          <p style="line-height: 1.4rem;">
            Unsure how much to charge? We advise you multiple your hourly rate by the amount of support you want
            to sell. For example, if you want to offer an 8-hour monthly plan and change $50/$50 per hour, then
            you should set the price to $400/£400.
          </p>
          <p style="line-height: 1.4rem;">
            <strong>Please note:</strong> you do not need to incorporate the price of the software; that will be added
            at checkout to calculate the final price.
          </p>
          <p style="line-height: 1.4rem;">
            However, a minimum price of $1/£1 is required to ensure successful product creation within Stripe.
          </p>
        </div>
      `;
    }
  },
  watch: {
    monthlyRequired() {
      if (
        !(this.monthlyRequired || this.annualRequired) &&
        this.enableSupportAddon
      ) {
        this.toggleSupportAddon();
      }
    },
    annualRequired() {
      if (
        !(this.monthlyRequired || this.annualRequired) &&
        this.enableSupportAddon
      ) {
        this.toggleSupportAddon();
      }
    }
  },
  async created() {
    // TODO: Before leaving, warn users of lost data
    await this.fetchSoftwarePrices();
  },
  methods: {
    ...mapActions('productManager', [
      'setSoftwareProduct',
      'setSoftwareProductField',
      'setSoftwareProductPriceField',
      'toggleSupportAddon',
      'toggleEnabledCurrency',
      'fetchSoftwarePrices',
      'setSupportAddonField',
      'fetchSoftwarePrices',
      'validateSoftwareProduct',
      'validateSupportAddon',
    ]),
    ...mapActions('addonManager', [
      'clearAddonConfiguration',
    ]),
    validateAndSubmit() {
      this.validateSoftwareProduct()
        .then(() => {
          this.validateSupportAddon()
            .then(() => this.$emit('submit'))
            .catch((e) => this.validationErrorMessage(e)
          );
        })
        .catch((e) => {
          this.validateSupportAddon();
          this.validationErrorMessage(e)
        });
    },
    validationErrorMessage() {
      Swal.fire({
        icon: "warning",
        title: "Whoops!",
        text: 'Sorry, there were some problems with your submission. '+
          'Please see the highlighted fields for more information.',
      });
    },
  },
}
</script>

<style lang="scss" scoped>
  .product {
    border-radius: 12px;

    .currency_toggle {
      margin-bottom: 1rem;
    }

    small {
      display: block;
      margin: 0.4rem 0 0 0;
      font-size: 0.75rem;
      color: #4E4E54;
    }

    h3 {
      font-weight: 700;
      font-size: 1.5rem;
      line-height: 2.125rem;
      margin-bottom: .5rem;
    }

    p {
      color: #4E4E54;
      margin-bottom: 0.5rem;
    }

    label {
      font-weight: 500;
      font-size: 1.125rem;
    }

    .select_type {
      cursor: pointer;
      font-weight: 500;

      &.currency {
        cursor: pointer;
        border: 1px solid #37B2A8;
        color: #37B2A8;
        border-radius: 12px;
        display: inline-block;
        padding: 0.5rem 2rem!important;

        &.active {
          background: #37B2A8;
          color: white;
        }

        &:first-child {
          margin-right: 1rem;
        }

        &.disabled {
          opacity: 0.4;

          .temp_radio {
            cursor: default;
          }
        }
      }

      label {
        font-size: 1.125rem;
        font-weight: 500;
        cursor: pointer!important;
      }
    }

    &.card {
      padding: 1rem;
      border: 1px solid #C7C4C1!important;

      .select_type {
        padding: .5rem 0;
        margin: 0;
      }

      &.support-toggle {
        padding: 0 1rem;
        text-align: center;

        p {
          margin: 1rem 0 0 0;
        }
      }
    }

    &.inline {
      display: flex;
      justify-content: center;
    }

    &.card, &.inline {
      &.support-toggle {
        &.disabled {
          .select_type {
            pointer-events: none;
            opacity: 0.2;
          }
        }
      }
    }

    &.inner {
      border: 0!important;
      background: #FBFAFA;
      box-shadow: none;
      margin-bottom: 0;

      h3 {
        margin: 0;
      }

      p {
        margin-bottom: 2rem;

        &.text-danger:last-of-type {
          margin-bottom: 0;
        }
      }

      h4 {
        font-size: 1.125rem;
        line-height: 1.625rem;
        font-weight: 500;
      }
    }

    &.support-toggle {
      margin-top: 1rem;
      padding: 1rem;
      text-align: center;
      flex-direction: column;

      .select_type {
        padding: 1rem 0;
        margin-bottom: 0;
      }
    }

    .currency_select {
      display: flex;
      align-items: center;
      justify-content: space-between;

      > div {
        > h4 {
          flex-grow: 1;
          font-size: 1.125rem;
          line-height: 1.625rem;
          font-weight: 500;
          margin-bottom: 0;
        }

        > small {
          margin-top: 0;
          margin-bottom: 1rem;
        }
      }
    }

    .currencies {
      margin-bottom: 1rem;
      transition: .3s;
    }
  }

  .temp_radio {
    display: inline-block;
    text-align: center;
    padding: 2px;
    border: 1px solid #37B2A8;
    cursor: pointer;
    margin-right: 15px;
    width: 25px;
    height: 25px;
    color: white;
    border-radius: 6px;

    svg {
      height: 20px;
      width: 15px;
    }

    &.active {
      background: #37B2A8;
      border: 0;
    }
  }

  .button {
    background: #37B2A8;
    border-radius: 12px;
    width: 100%;
    padding: 1rem;
    color: #fff;
    font-size: 1rem;
    font-weight: 700;

    &.info {
      color: #4E4E54;
      background: white;
      border: 2px solid #E8E5E2;
      border-radius: 12px;
      margin-bottom: 1rem;
    }
  }

  label {
    margin: 0;
  }

  .height_transition {
    transition: height 0.3s ease;
  }
</style>
